import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card';
import { usePunchListDashboard } from '../hooks/punch-list-hooks';
import type { FieldType } from '../types';

const DONUT_COLORS = {
  completed: '#22c55e',
  inProgress: '#3b82f6',
} as const;

const FIELD_TYPE_COLORS: Record<FieldType, string> = {
  Condition: 'bg-violet-500',
  'Yes/No': 'bg-sky-500',
  'Free Text': 'bg-amber-500',
  Photo: 'bg-rose-500',
  Signature: 'bg-emerald-500',
};

function DonutChart({
  segments,
  size = 120,
  strokeWidth = 18,
}: {
  segments: { value: number; color: string; label: string }[];
  size?: number;
  strokeWidth?: number;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((s, seg) => s + seg.value, 0);

  if (total === 0) {
    return (
      <svg width={size} height={size} className="mx-auto">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  }

  let offset = 0;
  const arcs = segments.map((seg) => {
    const pct = seg.value / total;
    const dash = pct * circumference;
    const gap = circumference - dash;
    const currentOffset = offset;
    offset += dash;
    return (
      <circle
        key={seg.label}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={seg.color}
        strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${gap}`}
        strokeDashoffset={-currentOffset}
        strokeLinecap="butt"
        className="transition-all duration-500"
      />
    );
  });

  return (
    <svg
      width={size}
      height={size}
      className="mx-auto"
      style={{ transform: 'rotate(-90deg)' }}
    >
      {arcs}
    </svg>
  );
}

function Skeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className="h-4 w-28 animate-pulse rounded bg-muted" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function PunchListsDashboard() {
  const { data, isLoading, fetchStatus } = usePunchListDashboard();

  const isActuallyLoading = isLoading && fetchStatus === 'fetching';

  if (isActuallyLoading) return <Skeleton />;

  if (!data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="flex items-center justify-center py-10">
              <span className="text-sm text-muted-foreground">No data available</span>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const donutSegments = [
    { value: data.completed, color: DONUT_COLORS.completed, label: 'Completed' },
    { value: data.inProgress, color: DONUT_COLORS.inProgress, label: 'In Progress' },
  ];

  const fieldEntries = Object.entries(data.itemsByFieldType) as [FieldType, number][];
  const maxFieldCount = Math.max(...fieldEntries.map(([, v]) => v), 1);
  const incomplete = data.totalItems - data.completedItems;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Card 1 – Completion Status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Completion Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <DonutChart segments={donutSegments} size={100} strokeWidth={14} />
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            {donutSegments.map((seg) => (
              <span key={seg.label} className="flex items-center gap-1 text-xs text-muted-foreground">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: seg.color }}
                />
                {seg.label} ({seg.value})
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Card 2 – Items by Field Type */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Items by Field Type</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {fieldEntries.map(([type, count]) => (
            <div key={type} className="flex items-center gap-2 text-xs">
              <span className="w-16 shrink-0 truncate text-muted-foreground">{type}</span>
              <div className="relative h-4 flex-1 rounded bg-muted">
                <div
                  className={`absolute inset-y-0 left-0 rounded ${FIELD_TYPE_COLORS[type]}`}
                  style={{ width: `${(count / maxFieldCount) * 100}%` }}
                />
              </div>
              <span className="w-6 text-right tabular-nums font-medium">{count}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Card 3 – Average Completion */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Average Completion</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-2">
          <div className="relative">
            <svg width={96} height={96}>
              <circle cx={48} cy={48} r={40} fill="none" stroke="#e5e7eb" strokeWidth={8} />
              <circle
                cx={48}
                cy={48}
                r={40}
                fill="none"
                stroke="#22c55e"
                strokeWidth={8}
                strokeLinecap="round"
                strokeDasharray={`${(data.averageCompletionPercent / 100) * 2 * Math.PI * 40} ${2 * Math.PI * 40}`}
                style={{ transform: 'rotate(-90deg)', transformOrigin: '48px 48px' }}
                className="transition-all duration-500"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-2xl font-semibold tabular-nums">
              {data.averageCompletionPercent.toFixed(1)}%
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">across {data.total} punch lists</p>
        </CardContent>
      </Card>

      {/* Card 4 – Incomplete Items */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Incomplete Items</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-2">
          <span className="text-4xl font-bold tabular-nums">{incomplete}</span>
          <span className="text-xs text-muted-foreground">items remaining</span>
          <span className="mt-2 text-xs text-muted-foreground">
            of {data.totalItems} total items
          </span>
        </CardContent>
      </Card>
    </div>
  );
}
